/**
 * atomberg-cart-edd.js
 *
 * Validates every cart item at the saved pincode and displays the highest EDD.
 * This file is the single source of truth for the cart's serviceability
 * verdict (`window.__atombergCartVerdict`) — atomberg-checkout-guard.js only
 * consults it, it does not run its own checks.
 *
 * Two independent checks, two different failure policies:
 *   - Atomberg serviceability API (only for items flagged
 *     `requires_serviceability_check`, read from [data-cart-item] DOM
 *     attributes) — a business rule layered on top of an otherwise-shippable
 *     product. Fails OPEN on error: an infra hiccup here never blocks a sale.
 *   - Clickpost deliverability + EDD (every item with a SKU, 10-minute
 *     localStorage cache shared with atomberg-delivery-check.js) — the only
 *     signal for whether a courier can physically reach the pincode. Fails
 *     CLOSED: an explicit "unserviceable" result, a Clickpost/network error,
 *     or a missing SKU are all treated as blocking, same as a confirmed no.
 *     If the Clickpost URL metafield itself is unset, only THIS check is
 *     skipped (fails open) — that's a sitewide config gap, not a per-request
 *     deliverability signal, and the serviceability check above still runs
 *     independently.
 *
 * If ANY item fails either check, show the product names in the error, hide
 * the EDD, and publish a blocking verdict on window.__atombergCartVerdict —
 * exposed via window.__atombergRunCartVerdictCheck so the checkout guard can
 * force and await a fresh check when no verdict exists yet for the pincode.
 * Otherwise show the latest delivery date across all items (the slowest item
 * decides when the order arrives) in the cart widget's result row.
 *
 * Re-runs on:
 *   – atomberg:pincode-saved    (user entered a pincode in the cart delivery check widget)
 *   – cart:update               (Horizon fires this after every cart add/remove/quantity change)
 *   – shopify:section:load      (theme editor live-reload)
 *
 * shop.metafields.atomberg.edd_use_backend_api (boolean, default false) swaps both
 * checks above for a single batched POST to {api_base_url}/api/edd/serviceability —
 * see getUseBackendEdd()/callBackendEddBatch(). Off/unset keeps the direct-to-Clickpost
 * + separate serviceability-check behaviour above unchanged.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergCartEddInit) return;
  w._atombergCartEddInit = true;

  var PINCODE_KEY = 'atomberg_pincode';
  var EDD_CACHE_KEY = 'atomberg_edd_cache';
  var EDD_TTL_MS = 10 * 60 * 1000;

  // A saved pincode can be carried over from the PDP or a previous visit —
  // the cart is the final serviceability checkpoint, so it must never
  // silently trust that pincode as already verified. Sets true only once an
  // explicit trigger (Check button, or Proceed forcing a fresh check) has
  // run this page load; automatic triggers (page load, cart:update, drawer
  // reopen) stay a no-op until then. See run()'s gate below.
  var hasCheckedThisLoad = false;

  /** @returns {string} */
  function getSavedPincode() {
    try {
      return localStorage.getItem(PINCODE_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  /**
   * @param {string} sku
   * @param {string} pincode
   * @returns {{minMs: number|null, maxMs: number|null} | null}
   */
  function readEddCache(sku, pincode) {
    if (!sku || !pincode) return null;
    try {
      var cache = JSON.parse(localStorage.getItem(EDD_CACHE_KEY) || '{}');
      var hit = cache[sku + '_' + pincode];
      if (hit && Date.now() - hit.ts < EDD_TTL_MS) return hit;
    } catch (_) {}
    return null;
  }

  /**
   * @param {string} sku
   * @param {string} pincode
   * @param {number|null} minMs
   * @param {number|null} maxMs
   */
  function writeEddCache(sku, pincode, minMs, maxMs) {
    if (!sku || !pincode) return;
    try {
      var cache = JSON.parse(localStorage.getItem(EDD_CACHE_KEY) || '{}');
      cache[sku + '_' + pincode] = { minMs: minMs, maxMs: maxMs, ts: Date.now() };
      var cutoff = Date.now() - EDD_TTL_MS;
      Object.keys(cache).forEach(function (k) {
        if (cache[k].ts < cutoff) delete cache[k];
      });
      localStorage.setItem(EDD_CACHE_KEY, JSON.stringify(cache));
    } catch (_) {}
  }

  /**
   * Find the Clickpost URL from the nearest [data-cart-edd-root] element.
   * @returns {string}
   */
  function getClickpostUrl() {
    var root = document.querySelector('[data-cart-edd-root]');
    return (root && root.getAttribute('data-adc-clickpost-url')) || '';
  }

  /** @returns {string} Atomberg API base URL from the cart delivery widget, or '' */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-cart-edd-root]');
    return (root && root.getAttribute('data-adc-api-base-url')) || '';
  }

  /**
   * Whether a flagged product with NO clean serviceability response (API error)
   * should block checkout. Off by default (fail open) — flipped on per store via
   * the shop.metafields.atomberg.serviceability_fail_closed metafield.
   * @returns {boolean}
   */
  function getServiceabilityFailClosed() {
    var root = document.querySelector('[data-cart-edd-root]');
    return !!(root && root.getAttribute('data-adc-serviceability-fail-closed') === 'true');
  }

  /**
   * Whether this shop has migrated EDD + serviceability checks to the
   * backend API — set via shop.metafields.atomberg.edd_use_backend_api. Off
   * by default so the direct-to-ClickPost path keeps running unchanged.
   * @returns {boolean}
   */
  function getUseBackendEdd() {
    var root = document.querySelector('[data-cart-edd-root]');
    return !!(root && root.getAttribute('data-adc-use-backend-edd') === 'true');
  }

  /**
   * Product ids of cart items flagged requires_serviceability_check, from the
   * server-rendered [data-cart-item] attributes (/cart.js has no metafields).
   * @returns {string[]}
   */
  function getFlaggedProductIds() {
    /** @type {string[]} */
    var ids = [];
    document
      .querySelectorAll('[data-cart-item][data-requires-serviceability="true"]')
      .forEach(function (el) {
        var id = el.getAttribute('data-product-id');
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
    return ids;
  }

  /**
   * Atomberg serviceability check for one product. Returns a distinct status so
   * the caller can apply the store's fail-open / fail-closed policy to an API
   * error separately from a clean "not serviceable" answer.
   * @param {string} apiBaseUrl
   * @param {string} productId
   * @param {string} pincode
   * @returns {Promise<'serviceable' | 'unserviceable' | 'error'>}
   */
  function callServiceability(apiBaseUrl, productId, pincode) {
    var url =
      apiBaseUrl +
      '/api/serviceability/check?product_id=' +
      encodeURIComponent(productId) +
      '&pincode=' +
      encodeURIComponent(pincode);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('serviceability check failed');
        return r.json();
      })
      .then(function (data) {
        return data && data.serviceable ? 'serviceable' : 'unserviceable';
      })
      .catch(function () {
        return 'error';
      });
  }

  /**
   * Call Clickpost's WMS Unified Serviceability API for a single SKU, through
   * the shared 10-minute EDD cache.
   *
   * Deliverability (Clickpost) and serviceability (the Atomberg API) are
   * different signals with different failure policies. The Atomberg API
   * fails OPEN on error (see `callServiceability`) because it's a business
   * rule layered on top of an otherwise-shippable product. Clickpost is the
   * only signal for whether the courier network can physically reach the
   * pincode at all, so any inconclusive result — an explicit Clickpost error
   * (`meta.success: false`), a network/parse failure, a missing SKU to check,
   * or a confirmed-deliverable response with NO edd.min/edd.max at all — is
   * treated as `status: 'error'` and BLOCKS checkout in `run()`, same as a
   * confirmed `'unserviceable'` response. A usable EDD range is required to
   * proceed, not just a bare "deliverable" flag.
   * @param {string} clickpostUrl
   * @param {string} pincode
   * @param {string} sku
   * @returns {Promise<{status: 'ok', minMs: number|null, maxMs: number|null} | {status: 'unserviceable'} | {status: 'error'}>}
   */
  function callClickpost(clickpostUrl, pincode, sku) {
    var cached = readEddCache(sku, pincode);
    if (cached) {
      return Promise.resolve(
        /** @type {{status: 'ok', minMs: number|null, maxMs: number|null}} */ ({
          status: 'ok',
          minMs: cached.minMs,
          maxMs: cached.maxMs,
        })
      );
    }
    return fetch(clickpostUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sku: sku, drop_pincode: pincode, wms_code: 'vinculum' }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        var meta = (data && data.meta) || {};
        if (!meta.success) return /** @type {{status: 'error'}} */ ({ status: 'error' });
        var result = (data && data.result) || {};
        var accounts = result.serviceable_accounts || [];
        var ok = accounts.some(function (/** @type {any} */ acc) {
          var s = (acc && acc.serviceable) || {};
          return s.PREPAID === true || s.COD === true;
        });
        if (!ok) return /** @type {{status: 'unserviceable'}} */ ({ status: 'unserviceable' });
        var edd = result.edd || {};
        var minDays = edd.min != null ? edd.min : edd.max;
        var maxDays = edd.max != null ? edd.max : edd.min;
        var now = Date.now();
        var minMs = minDays != null ? now + minDays * 86400000 : null;
        var maxMs = maxDays != null ? now + maxDays * 86400000 : null;
        // Deliverable per serviceable_accounts, but no EDD range at all —
        // treated as inconclusive, not "fine with no date to show".
        if (minMs == null && maxMs == null) {
          return /** @type {{status: 'error'}} */ ({ status: 'error' });
        }
        writeEddCache(sku, pincode, minMs, maxMs);
        return /** @type {{status: 'ok', minMs: number|null, maxMs: number|null}} */ ({
          status: 'ok',
          minMs: minMs,
          maxMs: maxMs,
        });
      })
      .catch(function () {
        return /** @type {{status: 'error'}} */ ({ status: 'error' });
      });
  }

  /**
   * Single batched POST to the backend EDD + serviceability endpoint —
   * replaces the flag-off path's N (Clickpost) + M (serviceability) calls
   * with exactly one request per pincode check. Items with no SKU can't be
   * checked at all (same as the flag-off path's per-item guard) so they're
   * excluded from the request and reported back as skippedNoSku.
   * @param {string} apiBaseUrl
   * @param {string} pincode
   * @param {Array<{product_id: number, sku: string, product_title: string}>} items
   * @param {string[]} flaggedIds
   * @returns {Promise<{results: Array<{item: any, status: string, serviceable: boolean|null, eddMinDays: number|null, eddMaxDays: number|null}>, skippedNoSku: Array<any>}>}
   */
  function callBackendEddBatch(apiBaseUrl, pincode, items, flaggedIds) {
    /** @type {Array<any>} */
    var sentItems = [];
    /** @type {Array<any>} */
    var payload = [];
    /** @type {Array<any>} */
    var skippedNoSku = [];

    items.forEach(function (item) {
      if (!item.sku) {
        skippedNoSku.push(item);
        return;
      }
      sentItems.push(item);
      var pid = String(item.product_id);
      /** @type {any} */
      var entry = { sku: item.sku, productId: pid };
      if (flaggedIds.indexOf(pid) !== -1) entry.checkServiceability = true;
      payload.push(entry);
    });

    if (!payload.length) return Promise.resolve({ results: [], skippedNoSku: skippedNoSku });

    return fetch(apiBaseUrl + '/api/edd/serviceability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: payload, pincode: pincode }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('backend EDD check failed');
        return r.json();
      })
      .then(function (data) {
        var apiResults = (data && data.results) || [];
        var results = sentItems.map(function (item, i) {
          var res = apiResults[i] || {};
          return {
            item: item,
            status: res.status || 'error',
            serviceable: res.serviceable != null ? res.serviceable : null,
            eddMinDays: res.eddMinDays != null ? res.eddMinDays : null,
            eddMaxDays: res.eddMaxDays != null ? res.eddMaxDays : null,
          };
        });
        return { results: results, skippedNoSku: skippedNoSku };
      });
  }

  /**
   * Render the cart verdict (checked row, not-serviceable row, or field + message) in cart delivery check widgets.
   * Cart widgets have no [data-adc-sku-map] child (that element is only injected by the PDP block).
   * Also resets the Check button — atomberg-delivery-check.js leaves it in a
   * "Checking…" loading state until this run settles.
   * @param {string} pincode
   * @param {boolean} show
   * @param {string} [errorMsg] - When provided, shows this error in [data-adc-message] and pre-fills the input.
   * @param {{eddMinMs?: number|null, eddMaxMs?: number|null, unserviceable?: boolean}} [detail] - eddMinMs /
   *   eddMaxMs: the cart's delivery range for the checked row; unserviceable: errorMsg names undeliverable
   *   items, shown in the not-serviceable row.
   */
  function setCartDeliveryCheckState(pincode, show, errorMsg, detail) {
    document.querySelectorAll('[data-atomberg-delivery-check]').forEach(function (el) {
      if (el.querySelector('[data-adc-sku-map]')) return; // PDP widget — skip
      var summary = /** @type {HTMLElement|null} */ (el.querySelector('[data-adc-summary]'));
      var formWrap = /** @type {HTMLElement|null} */ (el.querySelector('[data-adc-form-wrap]'));
      var inputEl = /** @type {HTMLInputElement|null} */ (el.querySelector('[data-adc-input]'));
      var messageEl = /** @type {HTMLElement|null} */ (el.querySelector('[data-adc-message]'));
      var actionBtn = /** @type {HTMLButtonElement|null} */ (el.querySelector('[data-adc-action]'));
      if (actionBtn) {
        actionBtn.disabled = false;
        actionBtn.textContent = 'Check';
      }
      // Never overwrite the field while the shopper is actively typing in it —
      // e.g. a stale async response resolving, or a cart:update resync,
      // landing mid-edit must not stomp on what they're currently entering.
      var inputHasFocus = inputEl && document.activeElement === inputEl;
      // Header, date and icons of the new layout — exposed by atomberg-delivery-check.js
      var v2 = /** @type {any} */ (el)._adcV2;

      if (show) {
        if (summary) summary.hidden = false;
        if (formWrap) formWrap.hidden = true;
        if (inputEl && !inputHasFocus) inputEl.value = pincode;
        if (messageEl) {
          messageEl.textContent = '';
          messageEl.hidden = true;
        }
        if (v2) {
          v2.checked(
            pincode,
            detail && detail.eddMinMs != null ? detail.eddMinMs : null,
            detail && detail.eddMaxMs != null ? detail.eddMaxMs : null
          );
        }
      } else if (v2 && errorMsg && detail && detail.unserviceable) {
        // The not-serviceable row carries the product-named message, so the message slot stays empty
        if (messageEl) {
          messageEl.textContent = '';
          messageEl.hidden = true;
        }
        if (inputEl && !inputHasFocus) inputEl.value = pincode;
        v2.notServiceable(pincode, errorMsg);
      } else {
        if (v2) v2.reset();
        if (summary) summary.hidden = true;
        if (formWrap) formWrap.hidden = false;
        // Pre-fill input only when showing an error so user can see what was checked.
        if (inputEl && !inputHasFocus) inputEl.value = errorMsg ? pincode : '';
        if (messageEl) {
          if (errorMsg) {
            messageEl.textContent = errorMsg;
            messageEl.className =
              'atomberg-delivery-check__message atomberg-delivery-check__message--error';
            messageEl.hidden = false;
          } else {
            messageEl.textContent = '';
            messageEl.className = 'atomberg-delivery-check__message';
            messageEl.hidden = true;
          }
        }
      }
    });
  }

  /**
   * @param {string[]} titles
   * @returns {string} "A" | "A and B" | "A, B and C"
   */
  function joinTitles(titles) {
    var named = titles.filter(Boolean);
    if (named.length <= 1) return named[0] || '';
    var last = named[named.length - 1];
    return named.slice(0, -1).join(', ') + ' and ' + last;
  }

  /**
   * Build a human-readable error string for a confirmed-unserviceable item.
   * @param {string[]} titles
   * @returns {string}
   */
  function buildUnserviceableMsg(titles) {
    var list = joinTitles(titles);
    if (!list) return 'Sorry, an item in your cart is not available at your pincode.';
    var verb = titles.filter(Boolean).length === 1 ? 'is' : 'are';
    return list + ' ' + verb + ' not available at your pincode.';
  }

  /**
   * Error string shown when deliverability could not be confirmed for one or
   * more cart items (Clickpost error, network failure, or missing SKU).
   * @returns {string}
   */
  function buildCheckFailedMsg() {
    return "Sorry, we couldn't fetch pincode details. Please try again.";
  }

  /**
   * Variant id -> { title } rendered by the cart root, where title is the
   * custom.variant_title metafield. /cart.js cannot carry a metafield — read it fresh
   * each time, since the cart section re-renders on every cart:update.
   * @returns {Record<string, {title?: string}>}
   */
  function readVariantInfo() {
    var el = document.querySelector('[data-cart-edd-root] [data-adc-variant-info]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (_) {
      return {};
    }
  }

  /**
   * Reports a settled cart check to atomberg-moengage-datalayer.js, which fires
   * check_delivery_time. Every settled verdict reports, including the automatic runs
   * on page load, cart:update, drawer open and Proceed — each one is a real answer
   * from the API, whether or not the shopper asked for it.
   * @param {string} pincode
   * @param {boolean} serviceable
   * @param {boolean} errored - Deliverability could not be confirmed either way
   * @param {any[]} [items] - /cart.js line items; absent when the cart itself failed to load
   * @param {boolean} [byShopper] - The cart's own Check drove this, not an automatic trigger
   */
  function reportCheck(pincode, serviceable, errored, items, byShopper) {
    var info = readVariantInfo();
    var list = items || [];
    document.dispatchEvent(
      new CustomEvent('atomberg:check-delivery', {
        detail: {
          pincode: pincode,
          page_type: 'cart',
          // Lets a dashboard count one row per shopper action; every refetch still reports
          trigger: byShopper ? 'user' : 'background',
          is_serviceable: serviceable,
          error: errored,
          // Parallel arrays, matching how view_cart reports multiple items
          product_name: list.map(function (it) {
            return it.product_title || it.title || '';
          }),
          product_variant: list.map(function (it) {
            return it.variant_title || '';
          }),
          variant_title: list.map(function (it) {
            return (info[String(it.variant_id)] || {}).title || '';
          }),
        },
        bubbles: true,
      })
    );
  }

  /**
   * Shared verdict + EDD-render logic for both the flag-off path (N Clickpost
   * + M serviceability calls) and the flag-on path (one batched backend
   * call) — only how `resolved`/`unserviceableTitles`/`checkFailedTitles`
   * get populated differs between them.
   * @param {Array<{minMs: number|null, maxMs: number|null}>} resolved
   * @param {string[]} unserviceableTitles
   * @param {string[]} checkFailedTitles
   * @param {string} pincode
   * @param {any[]} items - /cart.js line items, reported on the analytics event
   * @param {boolean} [byShopper] - The cart's own Check drove this run
   */
  function finishRun(resolved, unserviceableTitles, checkFailedTitles, pincode, items, byShopper) {
    // The saved pincode may have changed while these calls were in flight —
    // a slow run for an old pincode must never publish its verdict over the
    // current one (the checkout guard reads it by pincode and would
    // otherwise act on stale data).
    if (getSavedPincode() !== pincode) return;

    if (unserviceableTitles.length) {
      var msg = buildUnserviceableMsg(unserviceableTitles);
      setCartDeliveryCheckState(pincode, false, msg, { unserviceable: true });
      // atomberg-checkout-guard.js blocks Proceed on this verdict instantly.
      w.__atombergCartVerdict = { pincode: pincode, blocked: true, message: msg };
      reportCheck(pincode, false, false, items, byShopper);
      return;
    }

    if (checkFailedTitles.length) {
      // Deliverability couldn't be confirmed for at least one item (API
      // error, network failure, or no SKU) — block rather than assume it's
      // fine, since this is the only signal for whether a courier can
      // physically reach the pincode.
      var failMsg = buildCheckFailedMsg();
      setCartDeliveryCheckState(pincode, false, failMsg);
      w.__atombergCartVerdict = { pincode: pincode, blocked: true, message: failMsg };
      reportCheck(pincode, false, true, items, byShopper);
      return;
    }

    w.__atombergCartVerdict = { pincode: pincode, blocked: false, message: '' };
    reportCheck(pincode, true, false, items, byShopper);

    if (!resolved.length) {
      // Nothing to show an EDD for — either deliverability isn't configured
      // store-wide, or the cart is entirely flagged-serviceability-only
      // items with no EDD involvement. Not a block by itself.
      setCartDeliveryCheckState(pincode, true);
      return;
    }

    // The slowest item decides when the order arrives, so both ends of the range take the latest value
    /** @type {number|null} */
    var cartMinMs = null;
    /** @type {number|null} */
    var cartMaxMs = null;

    resolved.forEach(function (/** @type {{minMs: number|null, maxMs: number|null}} */ r) {
      if (r.minMs != null && (cartMinMs === null || r.minMs > cartMinMs)) {
        cartMinMs = r.minMs;
      }
      if (r.maxMs != null && (cartMaxMs === null || r.maxMs > cartMaxMs)) {
        cartMaxMs = r.maxMs;
      }
    });

    // Deliverability is already confirmed (blocked === false above) — show the
    // checked row even when no item returned day numbers; it just drops the date.
    setCartDeliveryCheckState(pincode, true, '', { eddMinMs: cartMinMs, eddMaxMs: cartMaxMs });
  }

  /**
   * Validates the saved pincode against every cart item and publishes the
   * result to `window.__atombergCartVerdict`. Returns a Promise so callers
   * (atomberg-checkout-guard.js) can force a fresh check and await it when no
   * verdict exists yet for the pincode about to be used at checkout.
   * @param {boolean} [isExplicit] - True for a user-initiated trigger (the Check
   *   button, or Proceed forcing a fresh check) — always runs. Automatic
   *   triggers (page load, cart:update, drawer reopen) omit this and are a
   *   no-op until an explicit check has happened at least once this load.
   * @param {boolean} [byShopper] - The cart's own Check drove this, which the analytics
   *   event reports as trigger 'user'. Drawer open and Proceed are explicit but not the
   *   shopper asking about delivery, so they stay 'background'.
   * @returns {Promise<void>}
   */
  function run(isExplicit, byShopper) {
    var pincode = getSavedPincode();

    if (!pincode || !/^\d{6}$/.test(pincode)) {
      setCartDeliveryCheckState('', false);
      w.__atombergCartVerdict = null;
      hasCheckedThisLoad = false;
      return Promise.resolve();
    }

    if (!isExplicit && !hasCheckedThisLoad) {
      // Saved pincode exists but hasn't been (re)confirmed on this page/drawer
      // load yet — leave the widget exactly as atomberg-delivery-check.js
      // rendered it (form visible, pincode pre-filled) rather than silently
      // showing it as already verified.
      return Promise.resolve();
    }
    hasCheckedThisLoad = true;

    // Read here, but NOT used to early-return — Clickpost and the Atomberg
    // serviceability API are independent checks with independent failure
    // policies. Short-circuiting the whole function when Clickpost isn't
    // configured would also silently skip the serviceability check for
    // flagged products even when THAT API is configured. Handled per-item
    // in the loop below instead.
    var clickpostUrl = getClickpostUrl();
    var useBackendEdd = getUseBackendEdd();

    return fetch('/cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        // The pincode may have been changed or cleared while this request was
        // in flight (the user editing the input, or the Edit button) — a
        // stale response must never overwrite what's on screen now.
        if (getSavedPincode() !== pincode) return;

        /** @type {Array<{product_id: number, sku: string, product_title: string}>} */
        var items = (cart && cart.items) || [];
        if (!items.length) {
          setCartDeliveryCheckState('', false);
          w.__atombergCartVerdict = null;
          return;
        }

        var apiBaseUrl = getApiBaseUrl();
        var flaggedIds = getFlaggedProductIds();
        var serviceabilityFailClosed = getServiceabilityFailClosed();

        // Flag-on path: one batched POST replaces the N (Clickpost) + M
        // (serviceability) calls below with exactly one request.
        if (useBackendEdd) {
          if (!apiBaseUrl) {
            // Flag on but no backend URL configured — same "couldn't confirm
            // deliverability" treatment as a hard API failure, since there's
            // no fallback path once the flag has opted the shop out of Clickpost.
            var noUrlMsg = "Sorry, we couldn't fetch pincode details. Please try again.";
            setCartDeliveryCheckState(pincode, false, noUrlMsg);
            w.__atombergCartVerdict = { pincode: pincode, blocked: true, message: noUrlMsg };
            return;
          }
          return callBackendEddBatch(apiBaseUrl, pincode, items, flaggedIds).then(function (batch) {
            /** @type {Array<{minMs: number|null, maxMs: number|null}>} */
            var resolved = [];
            /** @type {string[]} */
            var unserviceableTitles = [];
            /** @type {string[]} */
            var checkFailedTitles = [];

            batch.skippedNoSku.forEach(function (item) {
              if (checkFailedTitles.indexOf(item.product_title) === -1) {
                checkFailedTitles.push(item.product_title);
              }
            });

            batch.results.forEach(function (r) {
              var item = r.item;
              var pid = String(item.product_id);

              // Serviceability (flagged products only) — same fail-open/closed
              // policy as the flag-off path's callServiceability handling.
              if (flaggedIds.indexOf(pid) !== -1) {
                if (r.serviceable === false) {
                  if (unserviceableTitles.indexOf(item.product_title) === -1) {
                    unserviceableTitles.push(item.product_title);
                  }
                } else if (r.serviceable === null && serviceabilityFailClosed) {
                  if (checkFailedTitles.indexOf(item.product_title) === -1) {
                    checkFailedTitles.push(item.product_title);
                  }
                }
              }

              // Deliverability + EDD for every item — fails CLOSED, same as callClickpost.
              if (r.status === 'unserviceable') {
                if (unserviceableTitles.indexOf(item.product_title) === -1) {
                  unserviceableTitles.push(item.product_title);
                }
                return;
              }
              if (r.status === 'error') {
                if (checkFailedTitles.indexOf(item.product_title) === -1) {
                  checkFailedTitles.push(item.product_title);
                }
                return;
              }
              var now = Date.now();
              var minMs = r.eddMinDays != null ? now + r.eddMinDays * 86400000 : null;
              var maxMs = r.eddMaxDays != null ? now + r.eddMaxDays * 86400000 : null;
              writeEddCache(item.sku, pincode, minMs, maxMs);
              resolved.push({ minMs: minMs, maxMs: maxMs });
            });

            return finishRun(
              resolved,
              unserviceableTitles,
              checkFailedTitles,
              pincode,
              items,
              byShopper
            );
          });
        }

        /** @type {Array<{minMs: number|null, maxMs: number|null}>} */
        var resolved = [];
        /** @type {string[]} */
        var unserviceableTitles = [];
        /** @type {string[]} */
        var checkFailedTitles = [];
        /** @type {Array<Promise<void>>} */
        var fetches = [];

        items.forEach(function (item) {
          var pid = String(item.product_id);

          // Blocking serviceability check for flagged products — always fresh.
          // A clean "not serviceable" always blocks. An API error blocks only
          // when the store opts into fail-closed via the metafield; otherwise
          // it fails open so an outage never hard-blocks a paying customer.
          if (apiBaseUrl && flaggedIds.indexOf(pid) !== -1) {
            fetches.push(
              callServiceability(apiBaseUrl, pid, pincode).then(function (status) {
                if (status === 'unserviceable') {
                  if (unserviceableTitles.indexOf(item.product_title) === -1) {
                    unserviceableTitles.push(item.product_title);
                  }
                } else if (status === 'error' && serviceabilityFailClosed) {
                  if (checkFailedTitles.indexOf(item.product_title) === -1) {
                    checkFailedTitles.push(item.product_title);
                  }
                }
              })
            );
          }

          // Clickpost deliverability + EDD for every item — fails CLOSED.
          // Not configured store-wide → skip entirely (fail open for THIS
          // check only; the serviceability check above still ran).
          if (!clickpostUrl) return;

          // No SKU means we have no way to confirm deliverability at all,
          // so it's treated the same as an explicit Clickpost error.
          if (!item.sku) {
            if (checkFailedTitles.indexOf(item.product_title) === -1) {
              checkFailedTitles.push(item.product_title);
            }
            return;
          }
          fetches.push(
            callClickpost(clickpostUrl, pincode, item.sku).then(function (res) {
              if (res.status === 'unserviceable') {
                if (unserviceableTitles.indexOf(item.product_title) === -1) {
                  unserviceableTitles.push(item.product_title);
                }
              } else if (res.status === 'error') {
                if (checkFailedTitles.indexOf(item.product_title) === -1) {
                  checkFailedTitles.push(item.product_title);
                }
              } else {
                resolved.push(res);
              }
            })
          );
        });

        // MUST be returned — atomberg-checkout-guard.js awaits run()'s
        // promise to know the verdict has actually been set. Without this
        // return, run()'s promise resolved as soon as /cart.js was fetched,
        // before any Clickpost/serviceability call (or the verdict write)
        // had even started.
        return Promise.all(fetches).then(function () {
          return finishRun(
            resolved,
            unserviceableTitles,
            checkFailedTitles,
            pincode,
            items,
            byShopper
          );
        });
      })
      .catch(function () {
        if (getSavedPincode() !== pincode) return; // stale — see guard above
        // Couldn't even read the cart — no basis to confirm deliverability.
        var failMsg = "Sorry, we couldn't fetch pincode details. Please try again.";
        setCartDeliveryCheckState(pincode, false, failMsg);
        w.__atombergCartVerdict = { pincode: pincode, blocked: true, message: failMsg };
        reportCheck(pincode, false, true, [], byShopper);
      });
  }

  // Exposed so atomberg-checkout-guard.js can force a fresh check and await
  // it when no verdict exists yet for the pincode about to be used at
  // checkout (e.g. the user clicks Proceed before this run() call settles).
  // Always explicit — clicking Proceed is itself the user asking for a check.
  w.__atombergRunCartVerdictCheck = function () {
    return run(true);
  };

  function runAuto() {
    run();
  }

  function runExplicit() {
    run(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAuto);
  } else {
    runAuto();
  }

  // Pincode of a Check pressed in the cart itself, set just before it saves.
  var cartCheckPincode = '';
  document.addEventListener('atomberg:cart-check-requested', function (/** @type {any} */ e) {
    cartCheckPincode = ((e && e.detail) || {}).pincode || '';
  });

  // Only the cart's own Check runs the per-item fan-out. A PDP check saves the same
  // pincode, but the shopper is not looking at the cart — verifying N items then is work
  // nobody asked for, so it waits for the drawer to open (dialog:open below).
  document.addEventListener('atomberg:pincode-saved', function (/** @type {any} */ e) {
    if ((((e && e.detail) || {}).pincode || '') !== cartCheckPincode) return;
    cartCheckPincode = '';
    run(true, true);
  });
  // cart:update fires BEFORE morphSection('full') on the cart page, so setTimeout(0)
  // pushes run() into the next tick — after the synchronous morph replaces the DOM.
  // Automatic — only takes effect once an explicit check has happened this load.
  document.addEventListener('cart:update', function () {
    setTimeout(runAuto, 0);
  });
  document.addEventListener('shopify:section:load', runAuto);

  // Run when the cart drawer opens (dialog:open fires on cart-drawer-component, doesn't bubble).
  // Explicit, not auto — opening the drawer is the one moment this should always
  // verify a saved pincode, independent of whether an explicit check happened
  // earlier this page load (PDP visits/variant switches never trigger this file
  // at all now — see savePincode's silent param in atomberg-delivery-check.js).
  customElements.whenDefined('cart-drawer-component').then(function () {
    document.querySelectorAll('cart-drawer-component').forEach(function (el) {
      el.addEventListener('dialog:open', runExplicit);
    });
  });
})();
